import { Box, type BoxProps, Flex, Icon, useBreakpointValue } from "@chakra-ui/react";
import { useColorThemeToggle } from "hooks/use-color-theme-toggle";
import { type Theme, type ThemeBgColor } from "contexts/color-theme";
import React, { useId, useMemo, type PropsWithChildren } from "react";
import { horizontalContainerPadding } from "ui/container";

export type SolidPlateProps = PropsWithChildren<{
  id?: BoxProps["id"];

  /**
   * The color theme to use for text colors.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * The solid fill background color for the plate.
   * This is also used for the theme context (header/navigation theming).
   */
  bgColor: ThemeBgColor;

  /**
   * An optional custom background (e.g., gradient) to override the visual background.
   * When provided, this will be used for the actual background, but `bgColor` will
   * still be used for the theme context. Useful for one-off gradient instances.
   */
  customBg?: BoxProps["bg"];

  /**
   * An optional spotlight color to add at the top of the plate.
   * Usually, this is just one shade lighter than the background color,
   * and only used for the darkest color plates.
   */
  spotlightColor?: ThemeBgColor;

  /**
   * The maximum width of the plate content.
   *
   * @default "container.section"
   */
  maxW?: BoxProps["maxW"];

  /**
   * The vertical padding of the plate.
   *
   * @default { base: "biggest", lg: "huge" }
   */
  py?: BoxProps["py"];

  /**
   * The horizontal padding of the plate.
   *
   * @default `horizontalContainerPadding`
   */
  px?: BoxProps["px"];
}>;

/**
 * A full-width, solid colored plate with an optional spotlight effect.
 */
export function SolidPlate({
  id,
  children,
  spotlightColor,
  bgColor,
  customBg,
  theme = "light",
  maxW = "container.section",
  py = { base: "biggest", lg: "huge" },
  px = horizontalContainerPadding,
}: SolidPlateProps) {
  const scope = useColorThemeToggle({ theme, bgColor });

  return (
    <Box
      ref={scope}
      id={id}
      as="section"
      w="100%"
      pos="relative"
      overflow="hidden"
      borderRadius={{ base: "big", sm: "bigger", md: "biggest" }}
      bg={customBg ?? bgColor}
      color={`brand.text.${theme}`}
      py={py}
      px={px}
    >
      {spotlightColor && <SpotlightSvg color={spotlightColor} />}

      <Box pos="relative" w="100%" maxW={maxW} mt={spotlightColor ? { base: "big", lg: "biggest" } : 0} mx="auto">
        {children}
      </Box>
    </Box>
  );
}

/**
 * SVG to give the radial gradient effect of the spotlight on the plate.
 * Adjusts the spotlight automatically for mobile, tablet, and desktop views.
 */
function SpotlightSvg({ color }: { color: string }) {
  // Unique to avoid duplicate IDs in DOM if multiple spotlights are on the same page
  const clipPathId = useId();
  const filterId = useId();

  const desktopSpotlight = useMemo(
    () => (
      <Icon width="2512" height="726" viewBox="0 0 2512 726" fill="none">
        <g filter={`url(#${filterId})`} clipPath={`url(#${clipPathId})`}>
          <Box as="circle" cx="1256" cy="-758" r="1007" fill={color} />
        </g>
        <defs>
          <clipPath id={clipPathId}>
            <path fill="#fff" d="M0 0h2512v726H0z" />
          </clipPath>
          <filter
            id={filterId}
            x="-151"
            y="-726"
            width="2814"
            height="1452"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="200" result="effect1_foregroundBlur_235_2454" />
          </filter>
        </defs>
      </Icon>
    ),
    [color, clipPathId, filterId],
  );

  const tabletSpotlight = useMemo(
    () => (
      <Icon width="1872" height="726" viewBox="0 0 1872 726" fill="none">
        <g filter={`url(#${filterId})`} clipPath={`url(#${clipPathId})`}>
          <Box as="circle" cx="936.5" cy="-298.5" r="503.5" fill={color} />
        </g>
        <defs>
          <clipPath id={clipPathId}>
            <path fill="#fff" d="M0 0h1872v726H0z" />
          </clipPath>
          <filter
            id={filterId}
            x="33"
            y="-1202"
            width="1807"
            height="1807"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="200" result="effect1_foregroundBlur_235_2456" />
          </filter>
        </defs>
      </Icon>
    ),
    [color, clipPathId, filterId],
  );

  const mobileSpotlight = useMemo(
    () => (
      <Icon width="1296" height="708" viewBox="0 0 1296 708" fill="none">
        <g filter={`url(#${filterId})`} clipPath={`url(#${clipPathId})`}>
          <Box as="circle" cx="648" cy="59.925" r="224" fill={color} />
        </g>
        <defs>
          <clipPath id={clipPathId}>
            <path fill="#fff" d="M0 0h1296v708H0z" />
          </clipPath>
          <filter
            id={filterId}
            x="24"
            y="-564.075"
            width="1248"
            height="1248"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="200" result="effect1_foregroundBlur_235_2310" />
          </filter>
        </defs>
      </Icon>
    ),
    [color, clipPathId, filterId],
  );

  const spotlight = useBreakpointValue({
    base: mobileSpotlight,
    sm: tabletSpotlight,
    lg: desktopSpotlight,
  });

  return (
    <Flex pos="absolute" left="0" right="0" bottom="0" top={{ base: "-90px", md: "0" }} justifyContent="center">
      {spotlight}
    </Flex>
  );
}
